import React, { useState } from 'react'
import {
    ShowColumnButton,
    ExportButton,
    ImportListButton,
    FilterOrSortButton,
    MoveToListButton,
    ApproveButton,
} from './ActionButton'
import { useDispatch, useSelector } from 'react-redux'
import {
    openRejectAllSubmissionModal,
    REVIEW_MODAL_TYPE,
    selectMassSubmissionUuids,
    selectSelectedSubmissionView,
} from 'state/review'
import { selectIsWaitlistEnabled, selectRegistrationRemainingSlots } from 'state/registrations'
import _ from 'lodash'
import { GLOBAL_MODAL_TYPE, openModal } from 'state/constants'
import { useNewTranslation, useDisplayTooltip, useIsMobileView } from 'hooks'
import { ToolTipNames } from 'hooks/displayTooltip'
import styled from 'particles/themes/styled'
import { OptionsState } from 'particles/Dropdown'
import SearchBar from './SearchBar'
import RectangleButton from 'particles/Button/RectangleButton'
import Menu from 'particles/Menu'
import MenuItem from 'particles/MenuItem'
import MassModalWindow from 'components/Submissions/Review/common/MassModalWindow'
import { HandleMassSubmissionFunction } from 'components/Submissions/Review/common/types'
import { SubmissionFilter } from 'components/Submissions/Review/Submissions/Toolbar/Filter'
import { handleEditTagChange, handleRejectTagChange } from 'components/utils/review'
import { TableViewType } from '.'
import { useFilterModel } from './hooks'
import IconButton from 'particles/Button/IconButton'
import { SUBMISSION_VIEW_ENUM } from 'state/submissions'
import Divider from 'components/Shared/Divider'

interface HeaderBarProps {
    onShowColumnPanel: any
    onClickSubmissionToolbarApprove?: HandleMassSubmissionFunction
    onClickRegistrationToolbarApprove?: (e?: React.MouseEvent<HTMLElement>) => void
    onClickToolbarRequestEdit?: HandleMassSubmissionFunction
    onClickToolbarReject?: HandleMassSubmissionFunction
    onClickBriefRequirement?: () => void
    onClickToolbarMoveToWaitlist?: any
    enableImportList: boolean
    enableExport: boolean
    showFilter?: boolean
    showSort?: boolean
    showSearchBar?: boolean
    disableApprove?: boolean
    disableMoveToWaitlist?: boolean
    disableMoveBackToPending: boolean
    onClickMoveBackToPending?: any
    className?: string
    showMoreAction?: boolean
    onAddFilterField: (option: OptionsState) => void
    onAddSortField: (option: OptionsState) => void
    viewType?: TableViewType
}

const HeaderBar: React.FC<HeaderBarProps> = ({
    onShowColumnPanel,
    onClickBriefRequirement,
    onClickRegistrationToolbarApprove,
    onClickSubmissionToolbarApprove,
    onClickToolbarReject,
    onClickToolbarRequestEdit,
    onClickToolbarMoveToWaitlist,
    enableImportList,
    enableExport,
    showFilter = false,
    showSort = false,
    showSearchBar = true,
    disableApprove = false,
    disableMoveToWaitlist = false,
    showMoreAction = false,
    onClickMoveBackToPending,
    disableMoveBackToPending,
    onAddFilterField,
    onAddSortField,
    className,
    viewType,
}) => {
    const [sortAndFilterTooltip, handleUpdateTooltipState] = useDisplayTooltip()
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const isMobileView = useIsMobileView()
    const filterModel = useFilterModel(viewType)
    const interestRegistrationRemainingSlots = useSelector(selectRegistrationRemainingSlots)
    const isInterestRegistrationWaitlistEnabled = useSelector(selectIsWaitlistEnabled)
    const massSubmissionUuids = useSelector(selectMassSubmissionUuids)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const noFilterApplied =
        !filterModel ||
        (filterModel && filterModel.items && filterModel.items.length === 0) ||
        (filterModel && filterModel.items && filterModel.items.every(item => _.isEmpty(item.value)))
    const [moreActionsAnchorEl, setMoreActionsAnchorEl] = useState<null | HTMLElement>(null)
    const showCustomizeFilter =
        (selectedSubmissionView && selectedSubmissionView !== SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS) ||
        viewType === TableViewType.PERFORMANCE

    const handleOpenMoreActions = (event: React.MouseEvent<HTMLElement>) => {
        setMoreActionsAnchorEl(event.currentTarget)
    }
    const handleCloseMoreActions = () => {
        setMoreActionsAnchorEl(null)
    }

    const handleOnOpenModal = (type: GLOBAL_MODAL_TYPE) => () => {
        dispatch(openModal(type))
    }

    const getApproveButton = () => {
        if (onClickSubmissionToolbarApprove) {
            return (
                <MassModalWindow
                    type={REVIEW_MODAL_TYPE.APPROVE}
                    handleMassApprove={onClickSubmissionToolbarApprove}
                    selectedNum={massSubmissionUuids.length}
                />
            )
        }

        if (onClickRegistrationToolbarApprove) {
            return <ApproveButton onClick={onClickRegistrationToolbarApprove} isDisable={disableApprove} />
        }

        return null
    }

    const handleRejectPartiposter = () => {
        if (massSubmissionUuids?.length > 0) {
            dispatch(
                openRejectAllSubmissionModal({
                    isOpen: true,
                    selectedSubmissionUuids: massSubmissionUuids,
                }),
            )
        }
    }

    return (
        <div className={className}>
            <div className="default-actions">
                {selectedSubmissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS && (
                    <>
                        <RectangleButton
                            size="s"
                            color="neutral"
                            label={newT('button.remove')}
                            form="outlined"
                            icon="prohibit"
                            onClick={handleRejectPartiposter}
                            isDisabled={massSubmissionUuids.length <= 0}
                        />
                        <Divider height="24px" width="1px" className="divider" />
                    </>
                )}
                {onClickMoveBackToPending && (
                    <MoveToListButton
                        onClick={onClickMoveBackToPending}
                        isDisable={disableMoveBackToPending}
                        text={newT('button.moveBackToPending', 'Move back to pending')}
                    />
                )}
                {getApproveButton()}
                {onClickToolbarRequestEdit && (
                    <MassModalWindow
                        type={REVIEW_MODAL_TYPE.EDIT_REQUEST}
                        handleMassEditRequest={onClickToolbarRequestEdit}
                        selectedNum={massSubmissionUuids.length}
                        handleEditTagChange={handleEditTagChange}
                    />
                )}
                {onClickToolbarReject && (
                    <MassModalWindow
                        type={REVIEW_MODAL_TYPE.REJECT}
                        handleMassReject={onClickToolbarReject}
                        selectedNum={massSubmissionUuids.length}
                        handleRejectTagChange={handleRejectTagChange}
                    />
                )}
                {onClickToolbarMoveToWaitlist && (
                    <MoveToListButton
                        text={newT('button.moveToWaitlist', 'Move to waitlist')}
                        isWaitList
                        onClick={onClickToolbarMoveToWaitlist}
                        isDisable={disableMoveToWaitlist}
                        infoTitle={newT('submission.review.table.headerBar.moveToWaitlist.tooltip.title')}
                        infoLabel={newT('submission.review.table.headerBar.moveToWaitlist.tooltip.description')}
                    />
                )}
                {!showMoreAction && (
                    <div className="old-more-actions">
                        {enableExport && <ExportButton onClick={handleOnOpenModal(GLOBAL_MODAL_TYPE.REQUEST_EXPORT)} />}
                        {enableImportList ? (
                            !isInterestRegistrationWaitlistEnabled ||
                            (interestRegistrationRemainingSlots !== undefined &&
                                interestRegistrationRemainingSlots > 0) ? (
                                <ImportListButton
                                    onClick={handleOnOpenModal(GLOBAL_MODAL_TYPE.MASS_APPROVAL)}
                                    text={newT('button.importList')}
                                />
                            ) : (
                                <ImportListButton
                                    onClick={handleOnOpenModal(
                                        GLOBAL_MODAL_TYPE.INTEREST_REGISTRATION_WAITLIST_MASS_APPROVAL,
                                    )}
                                    text={newT('button.importWaitlist')}
                                />
                            )
                        ) : null}
                        <ShowColumnButton onClick={e => onShowColumnPanel(e, e?.target, false)} />
                    </div>
                )}
                {/* TODO: only enable filter & Sort feature on registration-result tables for now */}
                {showFilter && !showCustomizeFilter && (
                    <FilterOrSortButton
                        isActive={!noFilterApplied}
                        showList={sortAndFilterTooltip.current === ToolTipNames.GENERIC_FILTER}
                        onAddFilterField={onAddFilterField}
                        onClick={(e?: React.MouseEvent<HTMLElement>) => {
                            handleUpdateTooltipState(ToolTipNames.GENERIC_FILTER, e)
                        }}
                        anchorEl={sortAndFilterTooltip.anchorEl}
                        viewType={viewType}
                    />
                )}
                {/* TODO: This is temp filter for submissions (vote type filter) */}
                {/* Designer will help to update the submission filter in different ticket */}
                {showFilter && showCustomizeFilter && <SubmissionFilter />}
                {showSort && (
                    <FilterOrSortButton
                        isActive={!noFilterApplied}
                        showList={sortAndFilterTooltip.current === ToolTipNames.GENERIC_SORT}
                        onAddFilterField={onAddSortField}
                        isSort
                        onClick={(e?: React.MouseEvent<HTMLElement>) => {
                            handleUpdateTooltipState(ToolTipNames.GENERIC_SORT, e)
                        }}
                        anchorEl={sortAndFilterTooltip.anchorEl}
                    />
                )}
                {showMoreAction && (
                    <>
                        {isMobileView ? (
                            <IconButton
                                size="m"
                                form="icon"
                                icon="dotsThreeOutline"
                                iconForm="fill"
                                color="neutral"
                                onClick={e => {
                                    handleUpdateTooltipState(null)
                                    handleOpenMoreActions(e)
                                }}
                            />
                        ) : (
                            <RectangleButton
                                form="text"
                                size="s"
                                color="neutral"
                                label={newT('button.moreActions')}
                                icon="dotsThreeOutline"
                                iconForm="fill"
                                onClick={e => {
                                    handleUpdateTooltipState(null)
                                    handleOpenMoreActions(e)
                                }}
                            />
                        )}
                    </>
                )}
            </div>
            <div className="default-actions">
                {showSearchBar && (
                    <div>
                        <SearchBar viewType={viewType} />
                    </div>
                )}
                {showMoreAction && (
                    <Menu
                        size="s"
                        open={Boolean(moreActionsAnchorEl)}
                        anchorEl={moreActionsAnchorEl}
                        onClose={handleCloseMoreActions}
                        width={216}
                    >
                        {enableExport && (
                            <MenuItem.Text
                                icon="export"
                                text={newT('button.export')}
                                onClick={() => {
                                    handleOnOpenModal(GLOBAL_MODAL_TYPE.REQUEST_EXPORT)()
                                    handleCloseMoreActions()
                                }}
                            />
                        )}
                        {enableImportList ? (
                            !isInterestRegistrationWaitlistEnabled ||
                            (interestRegistrationRemainingSlots !== undefined &&
                                interestRegistrationRemainingSlots > 0) ? (
                                <MenuItem.Text
                                    icon="downloadSimple"
                                    text={newT('button.importList')}
                                    onClick={() => {
                                        handleOnOpenModal(GLOBAL_MODAL_TYPE.MASS_APPROVAL)()
                                        handleCloseMoreActions()
                                    }}
                                />
                            ) : (
                                <MenuItem.Text
                                    icon="downloadSimple"
                                    text={newT('button.importWaitlist')}
                                    onClick={() => {
                                        handleOnOpenModal(
                                            GLOBAL_MODAL_TYPE.INTEREST_REGISTRATION_WAITLIST_MASS_APPROVAL,
                                        )()
                                        handleCloseMoreActions()
                                    }}
                                />
                            )
                        ) : null}
                        <MenuItem.Text
                            text={newT('button.showColumns')}
                            icon="eye"
                            onClick={e => {
                                onShowColumnPanel(e, moreActionsAnchorEl, true)
                                handleCloseMoreActions()
                            }}
                        />
                        {onClickBriefRequirement && (
                            <MenuItem.Text
                                text={newT('button.viewSubmissionRequirements')}
                                icon="fileText"
                                onClick={() => {
                                    onClickBriefRequirement()
                                    handleCloseMoreActions()
                                }}
                            />
                        )}
                    </Menu>
                )}
            </div>
        </div>
    )
}

export default styled(HeaderBar)`
    display: flex;
    position: relative;
    align-items: center;
    padding: 12px 12px 4px 12px;
    justify-content: space-between;
    position: relative;
    .default-actions {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .old-more-actions {
        display: flex;
        button {
            min-width: fit-content;
        }
    }
    .divider {
        margin-left: 8px;
    }
`
